import { useCallback, useState } from "react";
import { AxiosError } from "axios";
import { downloadETaxItemPdf } from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../../components/SalesReportExport/utils";

export const ETAX_PDF_DOWNLOAD_ERROR_MESSAGE =
  "ไม่สามารถดาวน์โหลด PDF ได้ กรุณาลองใหม่อีกครั้ง";

// Shown when the backend reports the PDF is genuinely not in storage (HTTP 404):
// the file was never generated or has been removed. "Please try again" would be
// misleading here, so this case gets a specific, accurate message.
export const ETAX_PDF_NOT_FOUND_MESSAGE = "ไม่พบไฟล์ PDF ของรายการนี้";

/**
 * Shared handler for downloading a single E-Tax document PDF.
 *
 * The detail list and batch detail share this so that:
 * - a single row shows its own loading state (`downloadingId`);
 * - duplicate/concurrent clicks are ignored while a download is running;
 * - pending/unsent rows (no `etax_uuid`) never trigger a download;
 * - any failure (API error, missing file, or an invalid/empty file response)
 *   stops the loading state and surfaces a toast instead of leaving a spinner
 *   stuck or breaking the page.
 *
 * The PDF is streamed from the same-origin API (a binary blob) rather than
 * fetched from a presigned object-storage URL — the latter fails the browser's
 * cross-origin/CORS checks.
 */
export function useEtaxPdfDownload() {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const downloadPdf = useCallback(
    async (etaxUuid: string | null | undefined, filename: string) => {
      // Ignore rows with no generated e-tax item yet and block duplicate clicks.
      if (!etaxUuid || downloadingId) return;

      setDownloadingId(etaxUuid);
      try {
        const response = await downloadETaxItemPdf(etaxUuid);
        const blob = response.data as Blob;

        // Guard against an invalid file response (empty body or a JSON error
        // payload returned with a success status).
        if (
          !(blob instanceof Blob) ||
          blob.size === 0 ||
          blob.type.includes("application/json")
        ) {
          throw new Error("Invalid PDF file response");
        }

        const resolvedFilename =
          getFilenameFromContentDisposition(
            response.headers["content-disposition"] as string | undefined,
          ) || filename;

        downloadBlob(resolvedFilename, blob);
      } catch (error) {
        // A 404 means the file genuinely is not in storage — show the specific
        // "not found" message; anything else is treated as a retryable failure.
        const status = (error as AxiosError).response?.status;
        pushGlobalError(
          status === 404
            ? ETAX_PDF_NOT_FOUND_MESSAGE
            : ETAX_PDF_DOWNLOAD_ERROR_MESSAGE,
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [downloadingId],
  );

  return { downloadingId, downloadPdf };
}
